import {
  createColumnHelper,
  DeepKeys,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowData,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import startCase from 'lodash/fp/startCase';
import { ComponentProps, useMemo, useState } from 'react';
import styled from 'styled-components';

import Pagination from 'dashboard/components/Pagination';
import { renderAsString } from 'dashboard/components-lib/DataTable/helpers';
import TableControls from 'dashboard/components-lib/TableControls';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { AttributeColumn } from 'dashboard/models/table-view';
import TableViewConfigurationDropdown from 'dashboard/views/DashboardCasesView/TableViewConfigurationDropdown';
import { TableBody } from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';

const TableEmptyState = styled.td`
  vertical-align: middle;
  text-align: center;
  width: 100%;
  height: 176px;
`;

const isServerPagination = (
  pagination: boolean | ComponentProps<typeof Pagination> | undefined,
): pagination is ComponentProps<typeof Pagination> => {
  return typeof pagination === 'object';
};

interface ColumnConfig<TData extends RowData> {
  /**
   * Accessor to get the data from the row
   */
  accessor: DeepKeys<TData>;

  /**
   * Header to display for the column
   */
  header: string;
}

export type DataTableColumns<TData extends RowData> = Array<DeepKeys<TData> | ColumnConfig<TData>>;

interface DataTableProps<TData extends RowData> {
  /**
   * Data to render
   */
  data: TData[];

  /**
   * Columns to display in the table. May be a string or column config object.
   *
   * Strings get converted from camelCase to Start Case.
   */
  columns: DataTableColumns<TData>;

  /**
   * Whether to display table controls such as search and filters
   */
  showControls?: boolean;

  /**
   * Props to pass to the TableControls component
   */
  controls?: ComponentProps<typeof TableControls>;

  /**
   * Custom text to display when the table is empty. Will default to "No results found"
   */
  emptyState?: {
    title?: string;
    description?: string;
  };

  /**
   * Whether to display pagination controls. If true, will use client-side pagination. If an object, will use server-side pagination.
   */
  pagination?: boolean | ComponentProps<typeof Pagination>;

  sortable?: boolean;

  /**
   * Whether to display the column properties button and popover. Allows control of column visiblity and ordering.
   */
  showColumnProperties?: boolean;

  /**
   * Called when column visibility or ordering is updated. Useful for saving column properties.
   */
  onUpdateColumnProperties?: (newConfiguration: AttributeColumn[]) => void;
}

/**
 * The DataTable component is the core of Persona's table experiences.
 *
 * This component implements @tanstack/react-table and exposes a simplified, easy to use API.
 *
 * Planned functionality
 * - Column visibility, reordering
 * - Row selection
 * - Cell types (e.g. links, buttons, Stickers)
 *
 * Client-side support
 * ✅ Sorting
 * ❌ Filtering
 * ✅ Pagination
 *
 * Server-side support
 * ✅ Sorting
 * ✅ Filtering
 * ✅ Pagination
 */
const DataTable = <TData extends RowData>({
  data,
  columns,
  showControls,
  controls,
  emptyState,
  pagination,
  sortable = false,
  showColumnProperties = false,
  onUpdateColumnProperties,
}: DataTableProps<TData>) => {
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const visibility: { [key: string]: boolean } = {};
    columns.forEach((column) => {
      const columnId = typeof column === 'object' ? column.accessor : column;
      visibility[columnId] = true; // Set default visibility to true
    });
    return visibility;
  });
  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  const orderMap = columnOrder.reduce<{ [key: string]: number }>((acc, columnId, index) => {
    acc[columnId] = index;
    return acc;
  }, {});

  const tableOptions: Partial<TableOptions<TData>> = useMemo(() => {
    const options: Partial<TableOptions<TData>> = {};

    // Client-side pagination
    if (pagination === true) {
      options.getPaginationRowModel = getPaginationRowModel();
      options.initialState = {
        pagination: {
          pageSize: 10,
        },
      };
    } else if (isServerPagination(pagination)) {
      const currentPage = pagination.paginationParams?.['current-page'] || 1;
      const pageSize = pagination.pageSize || 10;

      options.manualPagination = true;
      options.onPaginationChange = (updaterOrValue) => {
        if (typeof updaterOrValue === 'function') {
          const newPaginationState = updaterOrValue({
            pageSize,
            pageIndex: currentPage - 1,
          });

          pagination.handlePageLinkClick(newPaginationState.pageIndex + 1);
          pagination.handlePageSizeClick(newPaginationState.pageSize);
        }
      };
      options.state = {
        pagination: {
          pageSize,
          pageIndex: currentPage - 1,
        },
      };
      options.rowCount = pagination?.paginationParams?.['total-count'];
      options.pageCount = pagination?.paginationParams?.['total-pages'];
    }

    if (options.state) {
      options.state.columnVisibility = columnVisibility;
      options.state.columnOrder = columnOrder;
    } else {
      options.state = {
        columnVisibility,
        columnOrder,
      };
    }

    options.onColumnVisibilityChange = setColumnVisibility;
    options.onColumnOrderChange = setColumnOrder;

    return options;
  }, [pagination, columnVisibility, columnOrder]);

  const [tableColumns, columnOptions] = useMemo(() => {
    const columnHelper = createColumnHelper<TData>();

    const tableColumns = columns.map((column) => {
      if (typeof column === 'object') {
        return columnHelper.accessor(column.accessor, {
          header: column.header,
        });
      }

      return columnHelper.accessor(column, { header: startCase(column) });
    });

    const columnOptions = columns.map((column) => {
      const key = typeof column === 'object' ? column.accessor : column;
      const label = typeof column === 'object' ? column.header : startCase(column);

      return {
        key,
        label,
      };
    });

    return [tableColumns, columnOptions];
  }, [columns]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: sortable ? getSortedRowModel() : undefined,
    enableSorting: sortable,
    ...tableOptions,
  });

  // We don't yet support multi-header groups across our products so we can assume there's only one
  const tableHeaderGroup = table.getHeaderGroups()[0];

  const rows = table.getRowModel().rows;

  return (
    <div>
      {showControls && controls && (
        <TableControls
          propertiesButton={
            showColumnProperties && (
              <TableViewConfigurationDropdown
                disabled={false}
                columnConfiguration={table
                  .getVisibleFlatColumns()
                  .map((column) => ({
                    key: column.id,
                    label: column.columnDef.header?.toString() || column.id,
                  }))
                  .sort((a, b) => {
                    return (orderMap[a.key] || 0) - (orderMap[b.key] || 0);
                  })}
                attributeOptions={columnOptions}
                updateColumnConfiguration={(newConfiguration) => {
                  table.getAllColumns().map((column) => {
                    column.toggleVisibility(!!newConfiguration.find((c) => c.key === column.id));
                  });

                  setColumnOrder(newConfiguration.map((c) => c.key));

                  if (onUpdateColumnProperties) {
                    onUpdateColumnProperties(newConfiguration);
                  }
                }}
              />
            )
          }
          {...controls}
        />
      )}
      <TableLayout
        headerTR={
          <>
            {tableHeaderGroup.headers.map((header) => (
              <TableTH
                id={header.id}
                key={header.id}
                sortable={header.column.getCanSort()}
                onClick={header.column.getToggleSortingHandler()}
                sortDirection={header.column.getIsSorted()}
              >
                {header.column.columnDef.header}
              </TableTH>
            ))}
          </>
        }
        nullState={<p>No results found.</p>}
        css={{ position: 'relative' }}
      >
        <TableBody>
          {rows.length === 0 ? (
            <TableEmptyState colSpan={tableHeaderGroup.headers.length}>
              <Paragraph size='lg' weight='bold' color='primary'>
                {emptyState?.title || 'No results found.'}
              </Paragraph>

              {emptyState?.description && (
                <Paragraph size='md' color='secondary'>
                  {emptyState.description}
                </Paragraph>
              )}
            </TableEmptyState>
          ) : (
            rows.map((row) => (
              <TableTR key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  // TODO (Amy): Add support for custom formats
                  <TableTD key={cell.id}>{renderAsString(cell.getValue())}</TableTD>
                ))}
              </TableTR>
            ))
          )}
        </TableBody>
      </TableLayout>
      {pagination && (
        <Pagination
          pageSize={table.getState().pagination.pageSize}
          paginationParams={{
            'current-page': table.getState().pagination.pageIndex + 1,
            'total-count': table.getRowCount(),
            'total-pages': table.getPageCount(),
          }}
          handlePageLinkClick={(pageNumber) => table.setPageIndex(pageNumber - 1)}
          handlePageSizeClick={table.setPageSize}
        />
      )}
    </div>
  );
};

export default DataTable;
